import { useAtomValue } from 'jotai';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { ParticipantStatus } from '../../types';
import { VideoPlatform, normalizeVideoPlatform } from '../../video-sdk';
import { useOpenConversationCallback } from '../chat/useOpenConversationCallback';
import { openWaitingRoomConversation } from '../clients-drawer/openWaitingRoomConversation';
import { useAdmitClientCallback } from '../clients-drawer/useAdmitClientCallback';
import { ProviderDrawerState, sessionConversationSidAtom, waitingRoomConversationSidsByVisitorIdAtom } from '../state';
import { ClientInformationDrawerFooter } from './ClientInformationDrawerFooter';

type ClientInformationDrawerFooterWrapperProps = {
  visitorUuid: string;
  visitorName: string;
  visitorStatus?: ParticipantStatus;
  patientShortId: string;
};

export function ClientInformationDrawerFooterWrapper({
  visitorUuid,
  visitorStatus,
  visitorName,
  patientShortId,
}: ClientInformationDrawerFooterWrapperProps) {
  const [admitClient, { loading: waitingForParticipantToJoin }] = useAdmitClientCallback();
  const isZoom = normalizeVideoPlatform(useVideoPlatform()) === VideoPlatform.Zoom;
  const getWaitingRoomConversationSids = useAtomCallbackMemoized(
    (get) => get(waitingRoomConversationSidsByVisitorIdAtom),
    [],
  );
  const sessionConversationSid = useAtomValue(sessionConversationSidAtom);
  const openConversation = useOpenConversationCallback();
  const onOpenWaitingRoomConversation = () =>
    openWaitingRoomConversation(visitorUuid, getWaitingRoomConversationSids, openConversation);
  const onOpenSessionConversation = () => {
    openConversation(ProviderDrawerState.SESSION_CHAT, sessionConversationSid);
  };

  const participantIsInSession = visitorStatus === ParticipantStatus.IN_SESSION;
  return (
    <ClientInformationDrawerFooter
      visitorUuid={visitorUuid}
      visitorName={visitorName}
      admitClient={participantIsInSession ? undefined : admitClient}
      isZoom={isZoom}
      visitorStatus={visitorStatus}
      waitingForParticipantToJoin={waitingForParticipantToJoin}
      patientShortId={patientShortId}
      openConversation={participantIsInSession ? onOpenSessionConversation : onOpenWaitingRoomConversation}
    />
  );
}
